import React, { useState, useMemo, useEffect } from "react";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import Loader from "../../UI/Loader/Loader";
import { toast } from "react-toastify";
import { useUserContext } from "../../../contexts/UserContext";
import { getAllTealiumProduct } from "../../../utils/APIs";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import AgGridWrapper from "../../UI/AgGrid";

const TealiumIntegrationModal = ({ handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [tealiumData, setTealiumData] = useState([]);
  const [selectedItem, setSelectedItem] = useState();
  const [gridApi, setGridApi] = useState(null);

  const { userData, updateDefaultTealiumKey } = useUserContext();
  const { content } = useContentfulContext();

  useEffect(() => {
    getTealiumDataHandler();
  }, []);

  const getTealiumDataHandler = async () => {
    try {
      setLoading(true);
      const tealium = await getAllTealiumProduct(
        userData.token,
        userData.selectedOrganization.org_token
      );
      const updated = tealium.data.map((x) => ({
        ...x,
        default: userData.defaultTealiumKey?._id == x._id ? true : false,
      }));
      setTealiumData(updated);
      // Set the default selected item
      const defaultItem = updated.find((item) => item.default);
      if (defaultItem) setSelectedItem(defaultItem);
    } catch (error) {
      toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelected = (event) => {
    const selectedRow = event.data;
    if (selectedItem?._id !== selectedRow._id) {
      setSelectedItem(selectedRow);
      toast.success(content.GLOBAL_SELECTOR_CONTENT.tealium.success_msg);
      updateDefaultTealiumKey(selectedRow);
    }
  };

  const CheckboxRenderer = (params) => {
    const isChecked = selectedItem?._id === params.data._id;
    return (
      <input
        type="checkbox"
        checked={isChecked}
        onChange={() => handleRowSelected(params)}
        className="cursor-pointer rounded-lg w-4 h-4"
      />
    );
};

  const colDefs = [
    {
      headerName: "",
      field: "checkbox",
      cellRenderer: CheckboxRenderer,
      suppressMovable: true,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.tealium.table.tealium_api_name,
      field: "tealium_api_name",
      cellRenderer: ({ data }) => (
        <button className="text-center block cursor-pointer text-primary">
          {data.tealium_api_name}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.tealium.table.tealium_account,
      field: "tealium_account",
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        {
          colId: "checkbox",
          minWidth: 40,
        },
        {
          colId: "tealium_api_name",
          minWidth: 300,
        },
        {
          colId: "tealium_account",
          minWidth: 350,
        },
      ],
    };
  }, []);

  const onFirstDataRendered = (params) => {
    if (tealiumData.length > 0) {
      const defaultRow = tealiumData.find((row) => row.default);
      if (defaultRow) {
        setSelectedItem(defaultRow);
      }
    }
  };

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    gridApi.setFilterModel({
      tealium_api_name: { type: "contains", filter: filterValue },
    });
  };

  return (
    <PrimaryModal
      title={content.GLOBAL_SELECTOR_CONTENT.tealium.title}
      hideModal={() => selectedItem && handleClose()}
      widthClass="max-w-5xl"
    >
      {loading && <Loader />}
      <div className="p-7 pt-1 space-y-6">
        <p>{content.GLOBAL_SELECTOR_CONTENT.tealium.description}</p>
        <div className="flex justify-between items-center">
          <input
            type="text"
            className="block px-2.5 py-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none max-w-64"
            placeholder="Search by name"
            onChange={onFilterTextBoxChanged}
          />
        </div>
        <div
          className={"ag-theme-quartz"}
          style={{ height: "100%", width: "100%" }}
        >
          <AgGridWrapper
            tableId="global-selector-tealium-integration-table"
            rowData={tealiumData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            rowSelection="single"
            suppressRowClickSelection
            onRowSelected={handleRowSelected}
          />
        </div>
      </div>
    </PrimaryModal>
  );
};

export default TealiumIntegrationModal;
